import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import PrivateImage from '../../components/common/PrivateImage';
import { 
  ArrowLeft,
  CheckCircle,
  XCircle,
  AlertCircle,
  Activity,
  Clock,
  Save,
  UserCircle
} from 'lucide-react';

const AttendanceControl = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [training, setTraining] = useState(null);
  const [players, setPlayers] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [participation, setParticipation] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const statuses = [
    { value: 'presente', label: 'Presente', icon: CheckCircle, color: 'bg-green-500 hover:bg-green-600', textColor: 'text-green-700', bgColor: 'bg-green-50' },
    { value: 'ausente', label: 'Ausente', icon: XCircle, color: 'bg-red-500 hover:bg-red-600', textColor: 'text-red-700', bgColor: 'bg-red-50' },
    { value: 'justificado', label: 'Justificado', icon: AlertCircle, color: 'bg-orange-500 hover:bg-orange-600', textColor: 'text-orange-700', bgColor: 'bg-orange-50' },
    { value: 'lesionado', label: 'Lesionado', icon: Activity, color: 'bg-purple-500 hover:bg-purple-600', textColor: 'text-purple-700', bgColor: 'bg-purple-50' },
    { value: 'tardanza', label: 'Tardanza', icon: Clock, color: 'bg-yellow-500 hover:bg-yellow-600', textColor: 'text-yellow-700', bgColor: 'bg-yellow-50' },
  ];

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);

      const { data: trainingData, error: trainingError } = await supabase
        .from('trainings')
        .select('*')
        .eq('id', id)
        .single();

      if (trainingError) throw trainingError;

      if (!trainingData) {
        navigate('/trainings');
        return;
      }

      setTraining(trainingData);

      const { data: playersData, error: playersError } = await supabase
        .from('players')
        .select('*')
        .eq('team', trainingData.team)
        .eq('active', true)
        .order('last_name', { ascending: true });

      if (playersError) throw playersError;

      setPlayers(playersData || []);

      const { data: attendanceData, error: attendanceError } = await supabase
        .from('attendance')
        .select('*')
        .eq('training_id', id);

      if (attendanceError) throw attendanceError;

      const attendanceMap = {};
      (attendanceData || []).forEach(att => {
        attendanceMap[att.player_id] = {
          status: att.status,
          notes: att.notes || '',
          id: att.id
        };
      });

      setAttendance(attendanceMap);

      // Cargar datos de participación (minutos, RPE, etc)
      const { data: participationData, error: participationError } = await supabase
        .from('training_participation')
        .select('*')
        .eq('training_id', id);

      if (participationError) throw participationError;

      const participationMap = {};
      (participationData || []).forEach(part => {
        participationMap[part.player_id] = {
          minutes_played: part.minutes_played || 0,
          individual_notes: part.individual_notes || '',
          attitude_rating: part.attitude_rating || 0,
          effort_rating: part.effort_rating || 0,
          rpe: part.rpe || 0,
          injuries_notes: part.injuries_notes || '',
          id: part.id
        };
      });

      setParticipation(participationMap);

    } catch (error) {
      console.error('Error al cargar datos:', error);
      setError('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = (playerId, status) => {
    setAttendance(prev => ({
      ...prev,
      [playerId]: {
        ...prev[playerId],
        status: status
      }
    }));
  };

  const handleNotesChange = (playerId, notes) => {
    setAttendance(prev => ({
      ...prev,
      [playerId]: {
        ...prev[playerId],
        notes: notes
      }
    }));
  };

  const handleParticipationChange = (playerId, field, value) => {
    setParticipation(prev => ({
      ...prev,
      [playerId]: {
        ...prev[playerId],
        [field]: value
      }
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');

      const attendanceRecords = Object.entries(attendance)
        .filter(([_, data]) => data.status)
        .map(([playerId, data]) => ({
          training_id: parseInt(id),
          player_id: parseInt(playerId),
          status: data.status,
          notes: data.notes || null,
          recorded_by: userProfile.id
        }));

      if (attendanceRecords.length === 0) {
        throw new Error('Debes marcar al menos un jugador');
      }

      // Guardar asistencia
      await supabase
        .from('attendance')
        .delete()
        .eq('training_id', id);

      const { error: insertError } = await supabase
        .from('attendance')
        .insert(attendanceRecords);

      if (insertError) throw insertError;

      // Guardar participación (minutos, RPE, etc) solo para jugadores presentes
      const participationRecords = Object.entries(participation)
        .filter(([playerId, _]) => {
          const status = attendance[playerId]?.status;
          return status === 'presente' || status === 'tardanza';
        })
        .map(([playerId, data]) => ({
          training_id: parseInt(id),
          player_id: parseInt(playerId),
          minutes_played: parseInt(data.minutes_played) || 0,
          individual_notes: data.individual_notes || null,
          attitude_rating: parseInt(data.attitude_rating) || null,
          effort_rating: parseInt(data.effort_rating) || null,
          rpe: parseInt(data.rpe) || null,
          injuries_notes: data.injuries_notes || null
        }));

      if (participationRecords.length > 0) {
        await supabase
          .from('training_participation')
          .delete()
          .eq('training_id', id);

        const { error: participationError } = await supabase
          .from('training_participation')
          .insert(participationRecords);

        if (participationError) throw participationError;
      }

      setSuccess(true);

      setTimeout(() => {
        navigate('/trainings');
      }, 2000);

    } catch (err) {
      console.error('Error al guardar asistencias:', err);
      setError(err.message || 'Error al guardar las asistencias');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    return timeString.substring(0, 5);
  };

  const getStats = () => {
    const stats = {
      total: players.length,
      presente: 0,
      ausente: 0,
      justificado: 0,
      lesionado: 0,
      tardanza: 0,
      sinMarcar: 0
    };

    players.forEach(player => {
      const status = attendance[player.id]?.status;
      if (status) {
        stats[status]++;
      } else {
        stats.sinMarcar++;
      }
    });

    return stats;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">¡Asistencias guardadas!</h2>
          <p className="text-gray-600 mb-4">Las asistencias se han registrado correctamente</p>
          <button
            onClick={() => navigate('/trainings')}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Volver a Entrenamientos
          </button>
        </div>
      </div>
    );
  }

  const stats = getStats();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/trainings')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Control de Asistencia</h1>
                {training && (
                  <p className="text-sm text-gray-600">
                    {training.team} - {formatDate(training.date)} ({formatTime(training.start_time)} - {formatTime(training.end_time)})
                  </p>
                )}
              </div>
            </div>

            <button
              onClick={handleSave}
              disabled={saving || stats.sinMarcar === stats.total}
              className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-5 h-5" />
              {saving ? 'Guardando...' : 'Guardar Asistencias'}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8">
          <div className="bg-white rounded-xl shadow-sm p-4 text-center">
            <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            <p className="text-sm text-gray-600">Total</p>
          </div>
          <div className="bg-green-50 rounded-xl shadow-sm p-4 text-center">
            <p className="text-2xl font-bold text-green-600">{stats.presente}</p>
            <p className="text-sm text-green-700">Presentes</p>
          </div>
          <div className="bg-red-50 rounded-xl shadow-sm p-4 text-center">
            <p className="text-2xl font-bold text-red-600">{stats.ausente}</p>
            <p className="text-sm text-red-700">Ausentes</p>
          </div>
          <div className="bg-orange-50 rounded-xl shadow-sm p-4 text-center">
            <p className="text-2xl font-bold text-orange-600">{stats.justificado}</p>
            <p className="text-sm text-orange-700">Justificados</p>
          </div>
          <div className="bg-purple-50 rounded-xl shadow-sm p-4 text-center">
            <p className="text-2xl font-bold text-purple-600">{stats.lesionado}</p>
            <p className="text-sm text-purple-700">Lesionados</p>
          </div>
          <div className="bg-yellow-50 rounded-xl shadow-sm p-4 text-center">
            <p className="text-2xl font-bold text-yellow-600">{stats.tardanza}</p>
            <p className="text-sm text-yellow-700">Tardanzas</p>
          </div>
        </div>

        {players.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center">
            <UserCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No hay jugadores</h3>
            <p className="text-gray-600">No hay jugadores registrados en este equipo</p>
          </div>
        ) : (
          <div className="space-y-4">
            {players.map((player) => {
              const playerAttendance = attendance[player.id] || {};
              const selectedStatus = playerAttendance.status;

              return (
                <div
                  key={player.id}
                  className={`bg-white rounded-xl shadow-sm p-6 transition-all ${
                    selectedStatus ? statuses.find(s => s.value === selectedStatus)?.bgColor : ''
                  }`}
                >
                  <div className="flex items-start gap-6">
                    <div className="flex-shrink-0">
                      {player.photo_url ? (
                        <PrivateImage
                          bucket="player-photos"
                          path={player.photo_url}
                          alt={`${player.first_name} ${player.last_name}`}
                          className="w-16 h-16 rounded-full object-cover"
                          fallbackIcon={UserCircle}
                        />
                      ) : (
                        <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center">
                          <UserCircle className="w-12 h-12 text-gray-400" />
                        </div>
                      )}
                      {player.player_number && (
                        <div className="mt-1 text-center">
                          <span className="inline-block px-2 py-0.5 bg-blue-600 text-white text-xs font-bold rounded">
                            #{player.player_number}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-bold text-gray-900 mb-1">
                        {player.first_name} {player.last_name}
                      </h3>
                      {player.position && (
                        <p className="text-sm text-gray-600 mb-3">{player.position}</p>
                      )}

                      <div className="flex flex-wrap gap-2 mb-3">
                        {statuses.map((status) => {
                          const StatusIcon = status.icon;
                          const isSelected = selectedStatus === status.value;

                          return (
                            <button
                              key={status.value}
                              onClick={() => handleStatusChange(player.id, status.value)}
                              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                                isSelected
                                  ? `${status.color} text-white shadow-md`
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                            >
                              <StatusIcon className="w-4 h-4" />
                              {status.label}
                            </button>
                          );
                        })}
                      </div>

                      {selectedStatus && (
                        <div className="space-y-3">
                          <input
                            type="text"
                            placeholder="Notas de asistencia (opcional)..."
                            value={playerAttendance.notes || ''}
                            onChange={(e) => handleNotesChange(player.id, e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />

                          {/* Campos de participación solo si está presente o con tardanza */}
                          {(selectedStatus === 'presente' || selectedStatus === 'tardanza') && (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                              <h4 className="text-sm font-semibold text-blue-900 mb-3">
                                📊 Datos de Participación
                              </h4>

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {/* Minutos jugados */}
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Minutos de participación
                                  </label>
                                  <input
                                    type="number"
                                    min="0"
                                    max="300"
                                    placeholder="0"
                                    value={participation[player.id]?.minutes_played || ''}
                                    onChange={(e) => handleParticipationChange(player.id, 'minutes_played', e.target.value)}
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  />
                                </div>

                                {/* RPE (1-10) */}
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    RPE - Esfuerzo percibido (1-10)
                                  </label>
                                  <select
                                    value={participation[player.id]?.rpe || ''}
                                    onChange={(e) => handleParticipationChange(player.id, 'rpe', e.target.value)}
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  >
                                    <option value="">Sin valorar</option>
                                    {[1,2,3,4,5,6,7,8,9,10].map(num => (
                                      <option key={num} value={num}>{num}</option>
                                    ))}
                                  </select>
                                </div>

                                {/* Actitud (1-5) */}
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Actitud (1-5)
                                  </label>
                                  <select
                                    value={participation[player.id]?.attitude_rating || ''}
                                    onChange={(e) => handleParticipationChange(player.id, 'attitude_rating', e.target.value)}
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  >
                                    <option value="">Sin valorar</option>
                                    <option value="1">1 - Muy mala</option>
                                    <option value="2">2 - Mala</option>
                                    <option value="3">3 - Regular</option>
                                    <option value="4">4 - Buena</option>
                                    <option value="5">5 - Excelente</option>
                                  </select>
                                </div>

                                {/* Esfuerzo (1-5) */}
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Esfuerzo (1-5)
                                  </label>
                                  <select
                                    value={participation[player.id]?.effort_rating || ''}
                                    onChange={(e) => handleParticipationChange(player.id, 'effort_rating', e.target.value)}
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  >
                                    <option value="">Sin valorar</option>
                                    <option value="1">1 - Muy bajo</option>
                                    <option value="2">2 - Bajo</option>
                                    <option value="3">3 - Regular</option>
                                    <option value="4">4 - Alto</option>
                                    <option value="5">5 - Máximo</option>
                                  </select>
                                </div>
                              </div>

                              {/* Notas individuales */}
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Notas individuales
                                </label>
                                <textarea
                                  placeholder="Observaciones sobre el rendimiento..."
                                  value={participation[player.id]?.individual_notes || ''}
                                  onChange={(e) => handleParticipationChange(player.id, 'individual_notes', e.target.value)}
                                  rows="2"
                                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                              </div>

                              {/* Molestias o lesiones */}
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Molestias o lesiones
                                </label>
                                <input
                                  type="text"
                                  placeholder="Ej: Molestias en rodilla izquierda..."
                                  value={participation[player.id]?.injuries_notes || ''}
                                  onChange={(e) => handleParticipationChange(player.id, 'injuries_notes', e.target.value)}
                                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AttendanceControl;